import { deepClone } from '@/pages/datalab/utils/utils';
import {
  langEn,
  langCn,
  sentimentIcon,
  GAME_MODE,
} from '@/pages/opinion/constants';
import * as Moment from 'moment';
import { extendMoment } from 'moment-range';
import type { ParamsTimeType } from '../types/common';
import { getLocale } from 'umi';
import { channelInfo } from '@op/components/ChannelLabel';
import { getRegionCountryDict } from './regionCountryDict';

/**
 * @param {number} value 数值
 * @param {boolean} isFixed 是否用保留两位小数
 */
export const echartCountMat = (value: number | string, isFixed?: boolean) => {
  if ([null, undefined, '', NaN].includes(value) || typeof value !== 'number')
    return '-';

  if (isFixed) {
    if (value >= 1000000000)
      return `${(Math.round(value / 10000000) / 100).toFixed(2)}B`;
    if (value >= 1000000)
      return `${(Math.round(value / 10000) / 100).toFixed(2)}M`;
    if (value >= 1000) return `${(Math.round(value / 10) / 100).toFixed(2)}K`;
    return (Math.round(value * 100) / 100).toFixed(2);
  }

  if (value >= 1000000000) return `${Math.round(value / 10000000) / 100}B`;
  if (value >= 1000000) return `${Math.round(value / 10000) / 100}M`;
  if (value >= 1000) return `${Math.round(value / 10) / 100}K`;
  if (value <= 10) {
    if (value.toString().length >= 10) {
      return Math.round((value + Number.EPSILON) * 100) / 100; // 解决 mentions/dau 坐标轴万分号前的数字精度缺失
    }
    return value;
  }

  return Math.round(value);
};

/**
 * TODO: 其他系统使用
 * 数字转换 过千保留一位小数显示K，过百万 保留一位小数显示M
 * @param value int
 * @returns
 */
export function getChartsShowCount(
  date: number,
  retain = 2,
  defaultValue = '0',
) {
  if (!date || typeof date !== 'number') {
    return defaultValue;
  }
  let value = Math.abs(date);
  const burden = date < 0 ? '-' : '';
  let unit = '';
  if (value >= 1000000000) {
    value = value / 1000000000;
    unit = 'B';
  } else if (value >= 1000000) {
    value = value / 1000000;
    unit = 'M';
  } else if (value >= 1000) {
    value = value / 1000;
    unit = 'K';
  }
  return burden + parseFloat(value?.toFixed(retain)) + unit;
}

/**
 * TODO: 其他系统使用
 * echarts 轴格式化
 * @param value
 * @param index
 * @returns
 */
export function echartsContent(value: any, issentment?: Boolean) {
  if (value == 0) {
    return 0;
  }
  if (value >= 1000000000) {
    return `${Math.round(value / 10000000) / 100}B`;
  }
  if (value >= 1000000) {
    return `${Math.round(value / 10000) / 100}M`;
  }
  if (value >= 1000) {
    return `${Math.round(value / 10) / 100}K`;
  }
  if (issentment) return Number(value).toFixed(2);
  return Math.round(value * 100) / 100;
}

/**
 * TODO: 其他系统使用
 * 数字转换 过千保留一位小数显示K，过百万 保留一位小数显示M
 * @param value
 * @returns
 */
export const mathFormat = (value: string | number) => {
  const newVal = Number(value);
  if (newVal === 0) return 0;
  if (!newVal) return '-';
  if (newVal >= 1000000000)
    return `${Math.round(newVal / 10000000 / 100).toFixed(2)}B`;
  if (newVal >= 1000000)
    return `${(Math.round(newVal / 10000) / 100).toFixed(2)}M`;
  if (newVal >= 1000) return `${(Math.round(newVal / 10) / 100).toFixed(2)}K`;
  return Math.round(newVal);
};

// 之前存在两种不同的展示方式，先都用起来

/**
 * 数字转换 过千保留一位小数显示K，过百万 保留一位小数显示M
 * @param value int
 * @returns
 */
export function getShowCount2(value: number) {
  if (value === 0) return '-';
  if (value >= 1000000) {
    const sen = value / 1000000;
    const num = sen.toFixed(1);
    return `${num}M`;
  }
  if (value >= 1000) {
    const sen = value / 1000;
    const num = sen.toFixed(1);
    return `${num}K`;
  }
  return value;
}

/**
 * TODO: 其他系统使用
 * 数值转KMB，超过1000才保留2位小数
 */
export function getShowCount(value: number) {
  if (value >= 1000000000) {
    const sen = value / 1000000000;
    const num = sen.toFixed(2);
    return `${num}B`;
  }
  if (value >= 1000000) {
    const sen = value / 1000000;
    const num = sen.toFixed(2);
    return `${num}M`;
  }
  if (value >= 1000) {
    const sen = value / 1000;
    const num = sen.toFixed(2);
    return `${num}K`;
  }
  return value;
}

const moment = extendMoment(Moment);

export const dateTypeMap: Record<ParamsTimeType, Moment.unitOfTime.Diff> = {
  monthly: 'month',
  weekly: 'week',
  daily: 'day',
  hourly: 'hour',
};

export function isOneDay({
  date_type,
  end_time,
  start_time,
}: {
  date_type: string;
  end_time: string;
  start_time: string;
}) {
  if (!(date_type === 'daily')) return false;
  return start_time?.split?.(' ')?.[0] === end_time?.split?.(' ')?.[0];
}

/**
 *
 * @param region 小写缩写
 * @param regionType  1地区，2国家,3语言，4中文国家，5中文区域
 *
 */
export function transRegion(region: string, regionType: number) {
  if (!region) return region;
  // 优先读运行时字典（接口数据 + 兜底常量合并），缺失自动回退写死常量
  const dict = getRegionCountryDict();
  const returnMap: {
    [key: number]: string;
  } = {
    1: dict.regionEn[region] || dict.countryEn[region.toLowerCase()],
    2: dict.countryEn[region.toLowerCase()],
    3: langEn[region.toLowerCase()],
    4: dict.countryCn[region.toLowerCase()],
    5: dict.regionCn[region] || dict.countryCn[region.toLowerCase()],
    6: langCn[region.toLowerCase()],
  };
  return returnMap[regionType] || region;
}

/**
 * 判断是否有这个国家或者地区
 * @param market
 * @returns 存在返回true，不存在返回false
 */
export function hasTheRegionOrCountry(market: string) {
  try {
    const countryKey = market?.toLocaleLowerCase();
    const regionKey = market?.toLocaleUpperCase();
    const dict = getRegionCountryDict();

    return !!dict.regionEn[regionKey] || !!dict.countryEn[countryKey];
  } catch (error) {
    return true;
  }
}

/**
 * 缩写转为字符串
 * 其中地区包含了国家的翻译
 * @param abbreviation  缩写
 * @param type          类型  region_en: 地区，country_en: 国家英文, lang_en:语言，country_cn: 国家中文
 * @returns
 */
export function transAbbreviation(
  abbreviation: string,
  type:
    | 'region_en'
    | 'region_cn'
    | 'country_en'
    | 'country_cn'
    | 'lang_en'
    | 'lang_cn',
  globalText = 'Unidentified',
) {
  // 加toLowerCase的是大小写脱敏处理
  const key = abbreviation?.toLowerCase?.();
  // 优先读运行时字典（接口数据 + 兜底常量合并），缺失自动回退写死常量
  const dict = getRegionCountryDict();
  let backText = abbreviation;
  if (type === 'region_en') {
    backText = dict.regionEn[abbreviation];
    backText = backText === 'Global' ? globalText : backText;
    if (!backText) backText = dict.countryEn[key];
  } else if (type === 'region_cn') {
    backText = dict.regionCn[abbreviation];
    backText = backText === 'Global' ? globalText : backText;
    if (!backText) backText = dict.countryCn[key];
  } else if (type === 'country_en') {
    backText = dict.countryEn[key];
  } else if (type === 'lang_en') {
    backText = langEn[key];
  } else if (type === 'lang_cn') {
    backText = langCn[key];
  } else if (type === 'country_cn') {
    backText = dict.countryCn[key];
  }
  return backText || abbreviation;
}

export function transLanguage(lang: string, local: string = getLocale()) {
  if (lang === 'Others') {
    return local === 'en-US' ? 'Others' : '其他';
  }
  return transAbbreviation(lang, local === 'zh-CN' ? 'lang_cn' : 'lang_en');
}

export function transRegionText(
  lang: string,
  globalText = 'Unidentified',
  local: string = getLocale(),
) {
  return transAbbreviation(
    lang,
    local === 'zh-CN' ? 'region_cn' : 'region_en',
    globalText,
  );
}

export function transCountryText(lang: string, local: string = getLocale()) {
  return transAbbreviation(
    lang,
    local === 'zh-CN' ? 'country_cn' : 'country_en',
  );
}

/**
 * TODO: 其他系统使用
 * 数组转对象
 * @param arrayInfo  数组对象
 * @param key
 * string : 取的arrayInfo中对象的值
 * function: 接收一个参数[arrayInfo数组对象中对象]，处理后返回key值
 *
 * eg：
 *   let arrayInfo = [{strKey: "info",fnKey: "name_info"}]
 *   let a = arrayToObj(arrayInfo, "strKey");
 *   a => {info: {strKey: "info",fnKey: "name_info"}}
 *   let b = arrayToObj(arrayInfo, (item) => item.split("_")[0])
 *   b => {name: {strKey: "info",fnKey: "name_info"}}
 * @returns
 */
export function arrayToObj(
  arrayInfo: Array<any>,
  key: string | ((param: any) => string),
) {
  try {
    return (arrayInfo || [])?.reduce((obj, current) => {
      const newKey: any =
        typeof key === 'function' ? key?.(current) : current?.[key];
      return {
        ...obj,
        [newKey]: current,
      };
    }, {});
  } catch (error) {
    console.log('数组转对象错误', error);
  }
  return {};
}

/**
 * 批量计算同比变化的函数
 * @param list            需要计算同比变化的属性key
 * @param calculateRatio  计算同比函数的方法
 */
function batchCalculateRatio(
  list: Array<string>,
  calculateRatio: (key: string) => { [key: string]: number },
) {
  return list.reduce((prev, curr) => {
    return { ...prev, ...calculateRatio(curr) };
  }, {});
}

/**
 * 创建计算同比变化的函数
 * @param item    新的对象
 * @param oldItem 老的对象
 * @returns (key: string) => ({ [key: string]: number }) 返回一个传key就能计算同比对象的函数
 */
function calculateRatioFn<T>(
  item: T,
  oldItem: T,
  keySuffix: string | ((arg0: string) => string) = '_than',
) {
  return (key: string) => {
    const newValue = item?.[key];
    const oldValue = oldItem?.[key];
    let value = !newValue ? 0 : 100;
    if (oldValue) {
      // 同比变化的计算公式： （新 - 旧）/ 旧,最终值为百分比，所以×100，然后4舍5入保留两位小数
      value = Number((((newValue - oldValue) / oldValue) * 100).toFixed(2));
    } else {
      value = 0;
    }
    // 获取真实key值
    const realKey =
      typeof keySuffix === 'string' ? key + keySuffix : keySuffix(key);
    return { [realKey]: value };
  };
}

/**
 * 创建计算总占比函数
 * @param item     数据对象
 * @param totalMap 总数对象
 * @returns (key: string) => ({ [key: string]: number }) 返回一个传key就能计算同比对象的函数
 */
function calculateTotalProgressFn<T>(
  item: T,
  totalMap?: { [key: string]: number },
  keyFn?: (value: string) => string,
  keySuffix: string | ((value: string) => string) = '_progress',
) {
  return (key: string) => {
    const newValue = item?.[key];
    // 获取总数的key值，如果没有，就直接使用key
    const totalKey = keyFn ? keyFn(key) : key;
    const total = totalMap?.[totalKey];
    let value = !newValue ? 0 : 100;
    if (total) {
      // 总占比的计算公式： value / total,最终值为百分比，所以×100，然后4舍5入保留两位小数
      value = Number(((newValue / total) * 100).toFixed(2));
    }
    // 获取真实key值
    const realKey =
      typeof keySuffix === 'string' ? key + keySuffix : keySuffix(key);
    return { [realKey]: value };
  };
}

/**
 * 处理有新老数据的情况，并添加同比变化的内容
 * 也可以处理占比数据
 * @param newList           新数据
 * @param oldList           老数据
 * @param onlyKey           唯一值
 * @param calculateKeys     需要参数计算的key值
 * @param otherDataDealwith 其他需要处理的数据，像Language，country等
 * @param totalMap          如果需要计算占比total的话，就需要传递改方法
 * @param totalKeyFn        total对应值在totalMap中的key值计算方法，默认是原key一样
 * @param ratioKeySuffix    计算出来的环比值添加的后缀
 *        string类型   元数据key: negative_mention 默认占比数据key: negative_mention_rate
 *        function类型 元数据key: negative_mention 默认占比数据key方法: (negative_mention) => key
 * @param totalKeySuffix    计算出来的占比值添加的后缀
 *        string类型   元数据key: negative_mention 默认占比数据key: negative_mention_rate
 *        function类型 元数据key: negative_mention 默认占比数据key方法: (negative_mention) => key
 * @returns
 */
export function dealwithNewOldData<T>(
  newList: Array<T>,
  oldList: Array<T>,
  onlyKey: string,
  calculateKeys: Array<string>,
  otherDataDealwith?: (item: T, oldItem?: T) => {},
  totalMap?: { [key: string]: number },
  totalKeyFn?: (value: string) => string,
  ratioKeySuffix: string | ((value: string) => string) = '_than',
  totalKeySuffix: string | ((value: string) => string) = '_progress',
) {
  if (!newList || newList.length === 0) return [];

  const oldMap = arrayToObj(oldList, onlyKey);
  return newList.map((item: any) => {
    const key = item[onlyKey];
    const thanMap = batchCalculateRatio(
      calculateKeys,
      calculateRatioFn<T>(item, oldMap?.[key], ratioKeySuffix),
    );

    let totalProgress = {};
    if (totalMap) {
      totalProgress = batchCalculateRatio(
        calculateKeys,
        calculateTotalProgressFn<T>(item, totalMap, totalKeyFn, totalKeySuffix),
      );
    }

    let otherMap = {};
    if (otherDataDealwith) {
      otherMap = otherDataDealwith(item, oldMap?.[key]);
    }

    return {
      ...item,
      ...thanMap,
      ...totalProgress,
      ...otherMap,
    };
  });
}

export function getSentimentIcon(sentiment: number) {
  const index = sentimentType(sentiment, true) as number;
  return sentimentIcon[index];
}

/**
 * TODO: 其他系统使用
 * sentiment基础颜色
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export const sentimentColorList = ['#69D19C', '#F17C86', '#6E7794'];

export const sentimentType = (
  sentiment: number,
  returnNum = false,
  isBrandHealth = false,
) => {
  const types: Array<[string, number]> = [
    ['positive', 1],
    ['negative', 2],
    ['neutral', 3],
  ];
  const sentimentRang = isBrandHealth ? [20, -20] : [3.5, 2.5];

  const index = returnNum ? 1 : 0;
  if (sentiment > sentimentRang[0]) return types[0][index];
  if (sentiment > sentimentRang[1]) return types[2][index];
  return types[1][index];
};

/**
 * TODO: 其他系统使用
 * 按时间添加没有的数据，并排序
 * 这个添加数据的方法【日期单个的情况下】
 * @param start_time  开始时间
 * @param end_time    结束时间
 * @param date_type   时间类型
 * @param list        数据列表
 * @param defaultObj  默认数据对象
 * @param timeKey     时间的key
 * @returns
 */
export function addWithoutSingleData(
  start_time: string,
  end_time: string,
  date_type: string,
  list: any[],
  defaultObj: {
    recommend?: number;
    review?: number;
    score?: number;
    sum_sentiment?: number;
    avg_sentiment?: number;
    mention?: number;
    brand_health?: number;
    negative_mention?: number;
    neutral_mention?: number;
    positive_mention?: number;
    ccv?: number;
    pcu?: number;
    revenue?: number;
    units?: number;
    key?: string;
    count?: number;
    negative_mentions?: number;
    neutral_mentions?: number;
    positive_mentions?: number;
  },
  timeKey = 'time',
) {
  const dateType = dateTypeMap[date_type];
  const format = dateType === 'hour' ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD';
  // 转为时间为key的对象
  const dateListObj = arrayToObj(list, (item) =>
    moment(item[timeKey]).format(format),
  );
  // 获取时间数组
  const dateArr = getTimeSpaceArray(start_time, end_time, dateType, format);

  const defaultArray: Array<any> = [];
  // 循环时间数组，如果发现时间key对象中没有，就放默认对象
  dateArr.forEach((time) => {
    const obj = dateListObj[time] || { [timeKey]: time, ...defaultObj };
    defaultArray.push(obj);
  });

  return defaultArray;
}

/**
 * TODO: 其他系统使用
 * 获取两个时间之间的差的中间的日期数组
 * @param start_time 开始时间的字符串
 * @param end_time   结束时间的字符串
 * @returns
 */
export function getTimeSpaceArray(
  start_time: string,
  end_time: string,
  rangeBy: Moment.unitOfTime.Diff = 'day',
  format = 'YYYY-MM-DD',
  noDealwith = false,
) {
  let s: any = moment(start_time);
  let e: any = moment(end_time);
  if (e.isBefore(s)) {
    console.error('开始时间大于结束时间');
    return [start_time];
  }

  // 对于周范围，使用 while 循环以获得更精确的控制
  if (rangeBy === 'week' && !noDealwith) {
    s = moment(s).day(1); // 设置为当周的第一天（周一）
    e = moment(e).day(7); // 设置为当周的最后一天（周日）

    const result = [];
    const current = moment(s);

    // 确保不超过原始结束时间
    const originalEnd = moment(end_time);
    while (current.isSameOrBefore(e) && current.isSameOrBefore(originalEnd)) {
      result.push(current.format(format));
      current.add(1, 'week');
    }

    return result;
  }
  // 对于其他范围单位，继续使用 moment-range
  const range = moment.range(s, e);
  return Array.from(range.by(rangeBy)).map((month) => month.format(format));
}

/**
 * 获取两个时间之间的差的中间的分钟数组
 * @param start_time 开始时间的字符串
 * @param end_time   结束时间的字符串
 * @returns
 */
export function getMinuteTimeSpaceArray({
  start_time,
  end_time,
  format = 'YYYY-MM-DD HH:mm:ss',
  interval = 5,
}: {
  start_time: string;
  end_time: string;
  format?: string;
  interval?: number;
}) {
  const s = moment(start_time);
  const e = moment(end_time);
  if (e.isBefore(s)) {
    console.error('开始时间大于结束时间');
    return [start_time];
  }

  const result: string[] = [];
  let current = s.clone();

  while (current.isSameOrBefore(e)) {
    result.push(current.format(format));
    current = current.add(interval, 'minutes');
  }

  return result;
}

/**
 * 添加占比数据
 * @param list              列表
 * @param keys              需要计算的key
 * @param total             总数 || 总数key
 * @param otherDealwithFun  其他数据处理方法，返回一个对象
 * @param keySuffix         计算出来的值添加的后缀
 *        string类型   元数据key: negative_mention 默认占比数据key: negative_mention_rate
 *        function类型 元数据key: negative_mention 默认占比数据key方法: (negative_mention) => string
 */
export function addRateValue<T>(
  list: Array<T>,
  keys: Array<string>,
  total: number | string,
  otherDealwithFun?: (item: T) => { [key: string]: any },
  keySuffix: string | ((value: string) => string) = '_rate',
) {
  const suffixIsString = typeof keySuffix === 'string';
  const isTotalKey = typeof total === 'string';
  return list.map((item: any) => {
    const addObj: Record<string, any> = {};
    keys.forEach((key) => {
      // 获取真实key值
      const realKey = suffixIsString ? key + keySuffix : keySuffix(key);
      // 获取真实的total
      const realTotal = isTotalKey ? item[total] : total;
      addObj[realKey] = getPercentageValue(item[key], realTotal, 10);
    });
    let otherObj = {};
    if (otherDealwithFun) {
      otherObj = otherDealwithFun(item);
    }
    return { ...item, ...addObj, ...otherObj };
  });
}

/**
 * TODO: 其他系统使用
 * 计算百分比值
 * @param value          数值
 * @param total          总数
 * @returns
 */
export function getPercentageValue(
  value: number,
  total: number,
  precision = 2,
) {
  if (total) {
    return Number((value / total).toFixed(precision));
  }

  return 0;
}

export function getPercentage(data: number, total: number, digit: number = 2) {
  if (total === 0) {
    return '-';
  }
  const preset = data / total;
  if (isNaN(preset)) {
    return '-';
  }

  return `${(preset * 100).toFixed(digit)}%`;
}

/**
 * 保留几位小数
 * @param value          数值
 * @param precision      精度
 * @param defaultValue   默认值
 * @param keepZero       四舍五入后末尾0是否保留 4.30 不保留=> 4.3
 * @returns
 */
export function retainDecimals(
  value: number,
  precision = 2,
  defaultValue = '-',
  keepZero = false,
) {
  let result: any = value?.toFixed(precision);
  if (!keepZero) {
    result = parseFloat(result);
  }
  return isNaN(result) ? defaultValue : result;
}

export function AUString(dataType: keyof typeof dateTypeMap) {
  const map: { [key in keyof typeof dateTypeMap]: string } = {
    weekly: 'W',
    monthly: 'M',
    daily: 'D',
    hourly: 'D',
  };
  return `${map[dataType] ?? 'D'}AU`;
}

export type ChartItemType = {
  count: number;
  name: string;
  name_cn: string;
  name_en: string;
  time: string;
  Child: null;
  [key: string]: any;
};

/**
 * TODO: 其他系统使用
 * 补齐单个缺失数据
 * @param start_time
 * @param end_time
 * @param basicData
 * @param date_type
 * @param allKey      所有key值,在知晓所有name属性的时候可直接提供
 */
export function supplementSingleData(
  start_time: string,
  end_time: string,
  basic_data: Array<ChartItemType>,
  date_type: string,
  allKey?: Array<string>,
): Array<ChartItemType> {
  const dateType = dateTypeMap[date_type];
  const format = dateType === 'hour' ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD';
  const dateArr = getTimeSpaceArray(start_time, end_time, dateType, format);
  const topics = allKey || getAllNameList(basic_data);
  const timeGroup = pressTimeGrouping(basic_data, format);
  const result = addWithoutItem(timeGroup, topics, dateArr);

  return result;
}

/**
 * 将数据分组处理
 * @param basicData
 * @param format 格式化时间
 * @returns
 */
export function pressKeyGrouping(basicData: Array<ChartItemType>) {
  const result: { [key: string]: any } = {};
  basicData?.forEach((item) => {
    const key = item.time;
    if (!result[key]) {
      result[key] = [];
    }
    result[key].push(item);
  });

  return result;
}

/**
 * 补齐单个缺失数据
 * @param start_time
 * @param end_time
 * @param basicData
 * @param date_type
 * @param allKey      所有key值,在知晓所有name属性的时候可直接提供
 */
export function supplementNoTimeSingleData(
  basic_data: Array<ChartItemType>,
  allKey?: Array<string>,
): Array<ChartItemType> {
  const dateArr = getAllNameList(basic_data, 'time');
  const topics = allKey || getAllNameList(basic_data);
  const timeGroup = pressKeyGrouping(basic_data);
  const result = addWithoutItem(timeGroup, topics, dateArr);
  return result;
}

/**
 * 获取所有的话题列表
 * @param basicData
 * @returns
 */
export function getAllNameList(basicData: Array<ChartItemType>, key = 'name') {
  if (!Array.isArray(basicData)) {
    return [];
  }
  const topics = basicData.map((item) => item[key]);
  const topicSet = new Set(topics);

  return Array.from(topicSet);
}

/**
 * 将数据按时间分组处理
 * @param basicData
 * @param format 格式化时间
 * @returns
 */
export function pressTimeGrouping(
  basicData: Array<ChartItemType>,
  format = "'YYYY-MM-DD'",
) {
  const result: { [key: string]: Array<ChartItemType> } = {};
  basicData?.forEach((item) => {
    const key = moment(item.time).format(format);
    if (!result[key]) {
      result[key] = [];
    }
    result[key].push(item);
  });

  return result;
}

/**
 * 添加没有的数据, 因为考虑到顺序问题，所以这里使用date进行循环
 * @param timeGroup 时间分组后的数据
 * @param topics 所有的话题
 */
export function addWithoutItem(
  timeGroup: {
    [key: string]: Array<ChartItemType>;
  },
  topics: Array<string>,
  dateArr: Array<string>,
) {
  const getOneTopic = (topic: string, timeOrObject: string | ChartItemType) => {
    const defaultObj = {
      count: 0,
      name: topic,
      name_cn: topic,
      name_en: topic,
      Child: null,
    };
    // 只给了一个时间，需要自己组装一个数据返回
    if (typeof timeOrObject === 'string') {
      return {
        ...defaultObj,
        time: timeOrObject,
      };
    }

    return {
      ...timeOrObject,
      value: 0,
      key: topic,
      ...defaultObj,
    };
  };

  const topicsLen = topics.length;
  const result: Array<ChartItemType> = [];
  dateArr?.forEach((key) => {
    const element = timeGroup[key] || [];
    // 一个元素都没有，那么就直接添加所有元素
    if (element.length === 0) {
      topics.forEach((item: string) => {
        result.push(getOneTopic(item, key));
      });
    } else if (element.length !== topicsLen) {
      // 有部分元素，需要进行比较，找出并添加
      topics.forEach((topic) => {
        if (element.findIndex((item) => item.name === topic) === -1) {
          result.push(getOneTopic(topic, element[0]));
        }
      });
    }
    // 长度相同就直接跳过了
    result.push(...element);
  });

  return result;
}

/**
 * 同时补齐多个缺失数据
 * @param start_time
 * @param end_time
 * @param basic_data
 * @returns
 */
export function supplementArrayData(
  start_time: string,
  end_time: string,
  basic_data: Array<{ timeline_of_mentions: Array<ChartItemType> }>,
  date_type: string,
): Array<any> {
  if (!basic_data) {
    return [];
  }

  return basic_data.map((item) => {
    if (item?.timeline_of_mentions) {
      item.timeline_of_mentions = supplementSingleData(
        start_time,
        end_time,
        item?.timeline_of_mentions || [],
        date_type,
      );
    }
    return item || [];
  });
}

const orderTimeMap = {
  desc: [-1, 1],
  asc: [1, -1],
};
/**
 * 时间排序的方法
 * @param timeKey 指定时间所在key值
 * @returns
 */
export function sortTimeListFunc(
  timeKey: string,
  order: 'desc' | 'asc' = 'asc',
) {
  return (a: any, b: any) => {
    const [before, after] = orderTimeMap[order];
    return moment(a[timeKey]).isAfter(moment(b[timeKey])) ? before : after;
  };
}

/**
 * 排时间数组的方法
 * @returns
 */
export const sortTimeArrayListFunc =
  (order: 'desc' | 'asc' = 'asc') =>
    (a: any, b: any) => {
      const [before, after] = orderTimeMap[order];
      return moment(a).isAfter(moment(b)) ? before : after;
    };

/**
 * TODO: 其他系统使用
 * 折线图data数据处理
 * @author liao sheng rong ⭐⭐⭐⭐⭐
 * @param {*} targetArr
 * @param needTime 是否需要日期数组 day格式为01-01 year格式为2022-01
 * @param keepZero 是否保留0
 * @returns newArr 处理好的折线图数据 timeArr 时间数组
 */
export const handleMentionData = (
  targetArr: any,
  needTime?: 'day' | 'year' | 'days' | 'hourly' | false,
  options = {},
  keepZero = true,
): {
  newArr: any[];
  timeArr: string[];
  totalArr: number[];
  customTotalArr: number[];
  dateCustomKeyTotal: Map<string, number>;
} => {
  const { dealwithCustomTotal, dealwithCustomTotalKey } = Object.assign(
    {
      dealwithCustomTotal: (v: { count: any }) => v.count,
      dealwithCustomTotalKey: (v: { time: string }) => v.time,
    },
    options,
  );
  const obj: any = {};
  const setimentObj: any = {};
  // 兼容上游未补齐时间点的场景：额外记录 series -> timeKey -> value/sentiment，
  // 在最后输出时按 timeArr 顺序对齐 data，缺失的时间点按 keepZero 语义补 0/null。
  // 对已经被 supplementSingleData 补齐的调用方，输出与原行为逐位一致。
  const seriesValueByTime: Record<string, Record<string, number>> = {};
  const seriesSentimentByTime: Record<string, Record<string, any>> = {};
  const newArr: Array<{
    name: string;
    type: string;
    data: any;
    sentiment: any;
  }> = [];
  const timeArr: string[] = [];
  let totalArr: number[] = [];
  let customTotalArr: number[] = [];
  const dateTotal = new Map();
  const dateCustomTotal = new Map();
  const dateCustomKeyTotal = new Map();
  let total = 0;
  let customTotal = 0;
  if (targetArr && targetArr.length > 0) {
    targetArr.sort(sortTimeListFunc('time'));
    targetArr.forEach((v: any) => {
      const seriesKey = v.name || v.keyword;
      if (!obj[seriesKey]) {
        obj[seriesKey] = [];
        setimentObj[seriesKey] = [];
        seriesValueByTime[seriesKey] = {};
        seriesSentimentByTime[seriesKey] = {};
      }
      obj[seriesKey].push(v.count);
      setimentObj[seriesKey].push(v.avg_sentiment);
      let timeKey: string;
      if (needTime) {
        const timeObj = {
          day: 'MM-DD',
          year: 'YYYY-MM',
          days: 'YYYY-MM-DD',
          hourly: 'YYYY-MM-DD HH:mm',
        };
        timeKey = moment(v.time).format(timeObj[needTime]);
        !timeArr.includes(timeKey) && timeArr.push(timeKey);
      } else {
        timeKey = v.time;
        !timeArr.includes(timeKey) && timeArr.push(timeKey);
      }
      seriesValueByTime[seriesKey][timeKey] = v.count;
      seriesSentimentByTime[seriesKey][timeKey] = v.avg_sentiment;
      if (dateTotal.has(v.time)) {
        total += v.count;
        dateTotal.set(v.time, total);
        customTotal += dealwithCustomTotal(v);
        dateCustomTotal.set(v.time, customTotal);
        dateCustomKeyTotal.set(dealwithCustomTotalKey(v), total);
      } else {
        total = v.count;
        dateTotal.set(v.time, total);
        customTotal = dealwithCustomTotal(v);
        dateCustomTotal.set(v.time, customTotal);
        dateCustomKeyTotal.set(dealwithCustomTotalKey(v), total);
      }
    });
    totalArr = [...dateTotal.values()];
    customTotalArr = [...dateCustomTotal.values()];

    // 根据总量进行排序
    const sorted = Object.fromEntries(
      Object.entries(obj).sort(([, arrA], [, arrB]) => {
        const sumA = arrA.reduce((s, v) => s + v, 0);
        const sumB = arrB.reduce((s, v) => s + v, 0);
        return sumB - sumA;
      }),
    );

    Object.keys(sorted).forEach((v) => {
      const valueMap = seriesValueByTime[v] || {};
      const sentimentMap = seriesSentimentByTime[v] || {};
      const data = timeArr.map((t) => {
        if (Object.prototype.hasOwnProperty.call(valueMap, t)) {
          const val = valueMap[t];
          return keepZero ? val : val || null;
        }
        return keepZero ? 0 : null;
      });
      const sentiment = timeArr.map((t) =>
        Object.prototype.hasOwnProperty.call(sentimentMap, t)
          ? sentimentMap[t]
          : undefined,
      );

      newArr.push({
        name: v,
        type: 'line',
        data,
        sentiment,
      });
    });
  }
  return { newArr, timeArr, totalArr, customTotalArr, dateCustomKeyTotal };
};

/**
 * 将数组转化为对象抛出
 * 和 handleMentionData 方法配合
 * @param data
 * @param needTime
 * @returns
 */
export const handleSumData = (
  data: Array<{ sum: number; x_axis: string }>,
  needTime: string,
) => {
  const timeObj = {
    day: 'MM-DD',
    year: 'YYYY-MM',
    days: 'YYYY-MM-DD',
    hourly: 'YYYY-MM-DD HH:mm',
  };
  let key: string | ((value: any) => string) = 'x_axis';
  if (needTime) {
    key = (item: { sum: number; x_axis: string }) => {
      const key = moment(item['x_axis']).format(timeObj[needTime]);
      return key;
    };
  }
  if (data) {
    return arrayToSimpleObj(data, 'sum', key);
  }

  return {};
};

/**
 * TODO: 其他系统使用
 * 转为简单对象
 * {
 *   key: valueKey
 * }
 * @param arrayInfo 数组
 * @param key  key对应的键 可以传入字符串，直接从对象中取，也可以传入方法，让方法计算返回key值
 * @param valueKey 值对应的键
 * @returns
 */
export function arrayToSimpleObj(
  arrayInfo: Array<any>,
  valueKey: string = 'text',
  key: string | ((any) => string) = 'value',
) {
  try {
    return (arrayInfo || [])?.reduce((obj, current) => {
      // 如果key是一个function，那么就将当前对象丢出去让其算出key出来，否则就是拿对象的值作为key
      const newKey: any =
        typeof key === 'function' ? key?.(current) : current?.[key];
      return {
        ...obj,
        [newKey]: current?.[valueKey],
      };
    }, {});
  } catch (error) {
    console.log('数组转对象错误', error);
  }
  return {};
}

/**
 * 参数转换成unidentified
 * @param obj
 */
export function transformGlobal(obj: any) {
  const newObj = deepClone(obj);

  const targetArr = [
    'mentions',
    'sentiment',
    'time_line_of_sentiment',
    'timeline_of_mentions',
    'sentiment_sort',
    'sentiment_stacked_column',
    'timeline_of_regions',
  ];
  targetArr.forEach((target) => {
    newObj?.[target]?.forEach((item: any) => {
      if (item.name === 'global' || item.name === 'GLOBAL')
        item.name = 'Unidentified';
    });
  });

  return newObj;
}

/**
 * TODO: 其他系统使用
 * 计算百分比字符串
 * @param value          数值
 * @param total          总数
 * @param precision      精度
 * @param defaultValue   默认值
 * @returns
 */
export function getPercentageInfo(
  value: number,
  total: number,
  precision = 0,
  defaultValue = '-',
  zeroDefaultValue = '0%',
) {
  if (total) {
    // 100%直接展示100%
    if (total === value) {
      return '100%';
    }

    if (value === 0) {
      return zeroDefaultValue;
    }
    return ((value / total) * 100).toFixed(precision) + '%';
  }

  return defaultValue;
}

/**
 * 对个别channel进行一个翻译
 * @param key 需要转译的channel字段
 * @returns 转译后的
 */
export function transChannel(key: string | number) {
  const channel: any = {
    youtube_keyword: 'youtube',
    google_keyword: 'Google News',
  };
  if (channel[key]) {
    return channel[key];
  }
  const { name } = channelInfo(key);
  return name;
}

/**
 * TODO: 其他系统使用
 * 计算环比
 * @param newValue       新值
 * @param oldValue       旧值
 * @param precision      精度
 * @returns
 */
export function getRationPercentValue(
  newValue: number,
  oldValue: number,
  precision = 2,
) {
  let value = !newValue ? 0 : 100;
  if (oldValue) {
    // 同比变化的计算公式： （新 - 旧）/ 旧,最终值为百分比，所以×100，然后4舍5入保留两位小数
    value = Number(
      (((newValue - oldValue) / Math.abs(oldValue)) * 100).toFixed(precision),
    );
  } else {
    value = 0;
  }

  return value;
}

/**
 * TODO: 其他系统使用
 * 字符串比较
 * @param str0  第一个字符串
 * @param str1  第二个字符串
 * @returns
 */
export function contrastString(str0: string, str1: string) {
  return str0?.toLocaleLowerCase?.() === str1?.toLocaleLowerCase?.();
}

/**
 * TODO: 其他系统使用
 * 首字母大写
 * @param content 内容
 * @returns
 */
export function initialUpperCase(content: string): string {
  if (typeof content !== 'string' || !content?.trim()) {
    return content;
  }

  try {
    return content[0]?.toUpperCase() + content.substr(1); // 首字母大写
  } catch (error) {
    return content;
  }
}

// 定顺序
const languageSort = ['origin', 'en', 'zh'];
export function getLanguageTypeColumns(
  languageType: Array<'en' | 'zh' | 'origin'>,
  languageSelectMap: any,
  render: Function,
) {
  // 通过过滤让列的顺序不变
  const languageList = languageSort.filter((a: any) =>
    languageType.includes(a),
  );
  const otherTableTab = languageList.map((key, index) => {
    const result = {
      key: `Reviews in ${languageSelectMap[key]}(Click to view details)`,
      header: `Reviews in ${languageSelectMap[key]}(Click to view details)`,
      width: 450,
      render: (cvm, o, index) => render(cvm, key, o, index),
    };
    if (index === 0) {
      result['fixed'] = 'left';
    }

    return result;
  });
  return otherTableTab;
}

// 数组去重
export function uniqueArray<T>(arr: Array<T>) {
  return Array.from(new Set(arr));
}

/**
 * @param dataSerie
 */
export function extraDataSerie(dataSerie) {
  let temp = [];
  dataSerie.forEach((v, i) => {
    v.data.forEach((v2, i2) => {
      if (i2 + 1 == v.data.length) {
        temp.push(v2);
      }
    });
  });
  const maxCount = Math.max.apply(null, temp);
  dataSerie.map((v, i) => {
    let dataSerieTemp: any = [];
    // let w = JSON.parse(JSON.stringify(v.data))
    v.data.map((v2, i2) => {
      if (i2 + 1 == v.data.length && v2 == maxCount) {
        dataSerieTemp.push({
          value: v2,
          label: {
            show: true,
            formatter: () => {
              return mathFormat(v2);
            },
          },
        });
      } else if (i2 + 1 == v.data.length) {
        dataSerieTemp.push({
          value: v2,
          label: { show: false },
        });
      } else {
        dataSerieTemp.push({
          value: v2,
          label: { show: false },
        });
      }
    });
    v.data = dataSerieTemp;
  });
  return dataSerie;
}

/**
 *
 * @param dataX

 * @returns
 */
export function extraDataX(dataX) {
  if (dataX) {
    let temp = [];
    dataX.forEach((v, i) => {
      if (i + 1 == dataX.length) {
        temp.push({ value: v, textStyle: { color: '#000000' } });
      } else {
        temp.push({ value: v });
      }
    });
    dataX = temp;
    return dataX;
  }
}

// 计算日期差
/***
 * ps 只选择一天或者一个星期或者一个月时使用
 * @param dateArry 日期数组
 * @param timeType 当前日期类型
 * @returns granularity
 */
export function dateDiff_copy(start_time, end_time, timeType) {
  let start_date = moment(start_time).min(0).seconds(0);
  let end_date = moment(end_time);
  let dateDiffs = end_date.diff(start_date, 'day');
  let ledateDiffs = end_date.diff(start_date, 'hour');
  let granularity = false;
  if (timeType === 'hourly') {
    return granularity;
  }
  if (timeType === 'daily') {
    granularity = ledateDiffs <= 24 ? true : false;
  } else if (timeType === 'weekly') {
    granularity = dateDiffs <= 6 ? true : false;
  } else {
    granularity = dateDiffs <= 30 ? true : false;
  }
  return granularity;
}

// 判断对象属性是否都是null 或者 ‘’
export const handleObjectIsNull = (obj: any) => {
  let isNull = false;
  let arrcopy = [];
  let arr = [];
  Object.values(obj || {}).forEach((item) => {
    //过滤掉为空的项
    if (item != '' && item != null && item != 0) {
      arr.push(item);
    } else {
      arrcopy.push(item);
    }
  });
  // 如果过滤后的arr长度为0，表示对象的键值对的值都为空！
  if (arr.length === 0) {
    isNull = true;
  } else {
    isNull = false;
  }
  return isNull;
};

export function localStoreSaveCountryToRegion(regionCountry: Array<any> = []) {
  const countryToRegion: { [key: string]: any } = {};
  const newRegionCountry = Array.isArray(regionCountry) ? regionCountry : [];
  newRegionCountry.forEach((regionItem) => {
    const { country, region_abbre, region_name_cn, region_name_en } =
      regionItem || {};
    (country || []).forEach(
      (countryItem: {
        country_name_abbre: any;
        country_name_cn: any;
        country_name_en: any;
      }) => {
        const { country_name_abbre, country_name_cn, country_name_en } =
          countryItem || {};
        countryToRegion[country_name_abbre] = {
          country_name_abbre,
          country_name_cn,
          country_name_en,
          region_abbre,
          region_name_cn,
          region_name_en,
        };
      },
    );
  });

  localStorage.setItem('countryRegion', JSON.stringify(countryToRegion));
}

export function handleNavigatorScroll(
  curNavigator: string,
  contentRef: {
    current: { scrollTo: (arg0: { top: number; behavior: string }) => void };
  },
  offset = 100,
) {
  const anchorElement = document.getElementById(curNavigator);
  if (!(anchorElement instanceof HTMLElement)) {
    return;
  }
  if (!(contentRef.current instanceof HTMLElement)) {
    return;
  }
  contentRef.current.scrollTo({
    top: anchorElement?.offsetTop - offset,
    behavior: 'smooth',
  });
}

/**
 * 处理内容滚动事件，确定当前滚动位置对应的元素
 * @param defaultElement - 默认返回的元素ID，当没有匹配元素时使用
 * @param elements - 需要检查的元素ID数组或单个元素ID
 * @param contentRef - 包含滚动内容的React ref对象
 * @param offset - 滚动偏移量阈值，默认为100
 * @returns 当前滚动位置对应的元素ID
 */
export function handleContentScroll(
  defaultElement: string,
  elements: string | any[],
  contentRef: { current: { scrollTop: any } },
  offset = 100,
) {
  let curOffsetElement = defaultElement;
  for (const element of elements) {
    const anchorElement = document.getElementById(element);
    if (!(anchorElement instanceof HTMLElement)) {
      continue;
    }
    if (!(contentRef.current instanceof HTMLElement)) {
      continue;
    }
    const curScrollTop = contentRef.current.scrollTop;
    if (curScrollTop >= anchorElement.offsetTop - offset) {
      curOffsetElement = element;
    }
  }
  return curOffsetElement;
}

export const scrollToAnchorPoint = (
  anchorName: string,
  block: 'start' | 'center' | 'end' | 'nearest' = 'start',
) => {
  if (anchorName) {
    // 找到锚点
    const anchorElement = document.getElementById(anchorName);
    // 如果对应id的锚点存在，就跳转到锚点
    if (anchorElement) {
      anchorElement.scrollIntoView({ block, behavior: 'smooth' });
    }
  }
};

/**
 * TODO: 其他系统使用
 * 按照sortMap的顺序进行排序的辅助方法
 * @param sortMap     排序对象
 * @param getKeyFunc  进行获取sortMap的key值方法
 * @param type  排序类型
 */
export function sortMapAuxiliaryFunc(
  sortMap: { [key: string]: number },
  getKeyFunc: (item: any) => string,
  type: 'asc' | 'desc' = 'asc',
) {
  return (a: any, b: any) => {
    const flag = type === 'asc' ? 1 : -1;

    return (sortMap[getKeyFunc(a)] - sortMap[getKeyFunc(b)]) * flag;
  };
}
/**
 * 按照sortArray的顺序进行排序的辅助方法
 * @param sortArray     排序数组
 */
export function sortArrayAuxiliaryFunc(sortArray: Array<any> | readonly any[]) {
  return (a: any, b: any) => {
    const aIndex = sortArray.indexOf(a);
    const bIndex = sortArray.indexOf(b);

    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;

    return aIndex - bIndex;
  };
}

export function arrayHasElement(arr: any) {
  return arr && Array.isArray(arr) && arr.length > 0;
}

export function transMode(mode: number) {
  return GAME_MODE?.[mode] || mode;
}
